<script setup>
import { computed, onMounted, onUnmounted, ref } from 'vue';
import ArticleReactions from './ArticleReactions.vue';
import ShareSheet from './ShareSheet.vue';

const props = defineProps({
	game: { type: Object, required: true },
	contentFiles: { type: Object, default: () => ({}) },
	user: { type: Object, required: true },
	handleArticleAction: { type: Function, required: true },
	openArticleSummary: { type: Function, required: true },
	isShareSheetOpen: Boolean,
	isSidebarVisible: Boolean,
	windowWidth: { type: Number, default: 0 },
	toggleModals: { type: Function, required: true },
	toggleSidebar: { type: Function, required: true },
	tagEvent: { type: Function, required: true },
});

const lightboxIndex = ref(-1);
const touchStartX = ref(0);
const previewImages = computed(() => props.game.preview || []);
const articleIndex = computed(() => Object.entries(props.contentFiles || {}).flatMap(([tag, section]) =>
	Object.entries(section.articles || {}).map(([id, article]) => ({ ...article, id, tag, section }))));
const relatedArticles = computed(() => {
	const relatedIds = props.game['related-articles'] || [];
	return relatedIds.map(id => articleIndex.value.find(article => article.id === id)).filter(Boolean);
});
const gameShareUrl = computed(() => {
	const url = new URL(window.location.href);
	url.search = '';
	url.searchParams.set('games', props.game.id);
	return url.toString();
});

function closeLightbox() { lightboxIndex.value = -1; }
function moveLightbox(direction) {
	if (previewImages.value.length) lightboxIndex.value = (lightboxIndex.value + direction + previewImages.value.length) % previewImages.value.length;
}
function onKeydown(event) {
	if (lightboxIndex.value < 0) return;
	if (event.key === 'Escape') closeLightbox();
	if (event.key === 'ArrowRight') moveLightbox(1);
	if (event.key === 'ArrowLeft') moveLightbox(-1);
}
function onTouchStart(event) { touchStartX.value = event.changedTouches[0].clientX; }
function onTouchEnd(event) {
	const difference = event.changedTouches[0].clientX - touchStartX.value;
	if (Math.abs(difference) > 40) moveLightbox(difference < 0 ? 1 : -1);
}
function imagePath(file) { return `/study/img/game-previews/${file}`; }
function logoPath(file) { return `/study/img/game-logos/${file}`; }
function bookmark(article) { props.handleArticleAction('saved', undefined, article.id); }
function articleSummaryHref(article) {
	const url = new URL(window.location.href);
	url.search = '';
	url.searchParams.set('article', article.id);
	return `${url.pathname}?${url.searchParams.toString()}`;
}
function openArticleSummary(event, article) {
	if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
	event.preventDefault();
	props.openArticleSummary(article);
}

onMounted(() => window.addEventListener('keydown', onKeydown));
onUnmounted(() => window.removeEventListener('keydown', onKeydown));
</script>

<template>
	<header class="game-hero" :style="{ '--logo-color': game['logo-color'] || '#596579' }">
		<img v-if="game.logo" class="hero-logo" :src="logoPath(game.logo)" :alt="`${game.title} logo`">
	</header>
	<div>
		<header class="game-detail-header">
			<div>
				<h1>{{ game.title }}</h1>
				<p class="game-source">{{ game.source }}</p>
			</div>
			<a class="btn primary play-button" :href="game.url" target="_blank" rel="noopener noreferrer">Play game</a>
		</header>
		<main class="game-detail-grid">
			<section class="game-detail-main">
				<ArticleReactions
					:isSidebarVisible="isSidebarVisible"
					:isShareSheetOpen="isShareSheetOpen"
					:isLiked="!!user?.training?.[game.id]?.like"
					:isDisliked="!!user?.training?.[game.id]?.dislike"
					:isSaved="!!user?.training?.[game.id]?.saved"
					:currentArticle="game"
					:localizedUrl="game.url"
					:shareUrl="gameShareUrl"
					:tagEvent="tagEvent"
					:targetUrl="game.url"
					:targetLabel="'Play Game'"
					:actionId="game.id"
					:isGame="true"
					:windowWidth="windowWidth"
					:toggleModals="toggleModals"
					:toggleSidebar="toggleSidebar"
					:handleArticleAction="handleArticleAction"
				/>
				<p class="game-summary" v-html="game.summary"></p>
				<div v-if="game.tags?.length" class="skills-section" aria-labelledby="skills-heading">
					<h2 id="skills-heading">Skills you’ll develop</h2>
					<ul class="skill-tags"><li v-for="tag in game.tags" :key="tag">{{ tag }}</li></ul>
				</div>
				<aside v-if="previewImages.length" class="preview-section" aria-label="Game previews">
					<header>
						<h2>Preview</h2>
						<span v-if="previewImages.length">({{ previewImages.length }} image{{previewImages.length !== 1 ? 's' : ''}})</span>
					</header>
					<div class="preview-row">
						<button v-for="(preview, index) in previewImages" :key="preview" class="preview-item" :aria-label="`Open preview ${index + 1}`" @click="lightboxIndex = index">
							<img :src="imagePath(preview)" :alt="`${game.title} preview ${index + 1}`" @error="$event.currentTarget.parentElement.classList.add('image-missing')">
							<span class="missing-preview">Preview image coming soon</span>
						</button>
					</div>
				</aside>
			</section>
			<!-- details col -->
			<section class="details-column" aria-labelledby="related-heading">
				<div class="related-section" aria-labelledby="related-heading">
					<h2 id="related-heading">Related articles</h2>
					<p v-if="!relatedArticles.length" class="muted">More reading will be added soon.</p>
					<article v-for="article in relatedArticles" :key="article.id" class="related-article">
						<a :href="articleSummaryHref(article)" class="related-title" @click="openArticleSummary($event, article)">{{ article.title }}</a>
						<span class="related-source">{{ article.source || article.section?.label }}</span>
						<button class="bookmark-button" type="button" :aria-label="user?.training?.[article.id]?.saved ? `Remove ${article.title} from saved` : `Save ${article.title}`" :aria-pressed="!!user?.training?.[article.id]?.saved" @click="bookmark(article)">
							<i :class="user?.training?.[article.id]?.saved ? 'fa-solid fa-bookmark' : 'fa-regular fa-bookmark'" aria-hidden="true"></i>
						</button>
					</article>
				</div>
			</section>
		</main>
	</div>
	<div v-if="lightboxIndex >= 0" class="lightbox" role="dialog" aria-modal="true" :aria-label="`${game.title} preview`" @click.self="closeLightbox" @touchstart="onTouchStart" @touchend="onTouchEnd">
		<button class="lightbox-close" aria-label="Close preview" @click="closeLightbox"><i class="fa-solid fa-xmark"></i></button>
		<button v-if="previewImages.length > 1" class="lightbox-arrow previous" aria-label="Previous image" @click="moveLightbox(-1)"><i class="fa-solid fa-chevron-left"></i></button>
		<img :src="imagePath(previewImages[lightboxIndex])" :alt="`${game.title} preview ${lightboxIndex + 1}`">
		<button v-if="previewImages.length > 1" class="lightbox-arrow next" aria-label="Next image" @click="moveLightbox(1)"><i class="fa-solid fa-chevron-right"></i></button>
		<span class="lightbox-count">{{ lightboxIndex + 1 }} / {{ previewImages.length }}</span>
	</div>
	<ShareSheet :isShareSheetOpen="isShareSheetOpen" :currentArticle="game" :tagEvent="tagEvent" :toggleModals="toggleModals" :shareUrl="gameShareUrl" />
</template>

<style lang="scss" scoped>
.game-hero { height: 300px; box-sizing: border-box; display: flex; align-items: center; justify-content: center; margin: -30px calc(-1 * var(--container-padding)) 30px; padding: 28px var(--container-padding); background: color-mix(in srgb, var(--logo-color) 80%, transparent); }
.hero-logo { display: block; border-radius: 20px; max-width: min(100%, 760px); max-height: 100%; object-fit: contain; }
.game-detail-header { display: flex; justify-content: space-between; flex-wrap: wrap; gap: 1em; margin-bottom: 1.5em; margin-top: 3rem; margin-inline: auto; max-width:1100px; }
.game-detail-header h1 { margin: 4px 0; font-size: clamp(2rem, 5vw, 3rem); line-height: 1.1em; }
.game-detail-header p { margin-block: .4em 0; }

.game-detail-grid { display: grid; grid-template-columns: minmax(0, 1fr); gap: 28px; margin-inline: auto; max-width:1100px; }
.game-detail-main { min-width: 0; }
.game-source { color: var(--text2); font-size: .82rem; font-weight: 700; letter-spacing: .04em; text-transform: uppercase; }
.game-summary { color: var(--text2); line-height: 1.65; margin: 18px 0 22px; }
.play-button { min-height: 32px; align-self: flex-start; margin-top: .4rem; }
.details-column h2, .preview-section h2 { margin: 0 0 12px; font-size: 1.25rem; }
.skills-section { margin-bottom: 28px; }
.skill-tags { display: flex; flex-wrap: wrap; gap: 8px; margin: 0; padding: 0; list-style: none; }
.skill-tags li { border: 1px solid var(--border); border-radius: 999px; padding: 6px 10px; background: var(--bg1); color: var(--text1); font-size: .88rem; line-height: 1.25; }

.preview-section { margin-top: 34px; }
.preview-section header {display: flex; flex-wrap: wrap; gap: 0.6rem; align-items: baseline; }
.preview-row { display: flex; gap: 14px; overflow-x: auto;     margin-inline: calc(-1 * var(--container-padding)); padding: 2px var(--container-padding) 14px; scrollbar-width: thin; }
.preview-item { position: relative; flex: 0 0 auto; width: min(350px, 85vw); aspect-ratio: 16 / 10; border: 1px solid var(--border); border-radius: 12px; overflow: hidden; padding: 0; background: var(--bg1); cursor: zoom-in; }
.preview-item img { width: 100%; height: 100%; display: block; object-fit: cover; }
.missing-preview { display: none; color: var(--text2); }
.preview-item.image-missing img { display: none; }
.preview-item.image-missing .missing-preview { display: grid; width: 100%; height: 100%; place-items: center; }
.lightbox { position: fixed; inset: 0; z-index: 50; display: flex; justify-content: center; align-items: center; background: rgba(0,0,0,.92); padding: 56px 64px; }
.lightbox > img { max-width: min(100%, 1200px); max-height: 100%; object-fit: contain; }
.lightbox-close, .lightbox-arrow { position: absolute; display: grid; place-items: center; width: 44px; height: 44px; border: 0; border-radius: 50%; background: rgba(255,255,255,.15); color: white; font-size: 1.2rem; cursor: pointer; }
.lightbox-close { top: 16px; right: 16px; }
.lightbox-arrow.previous { left: 14px; }
.lightbox-arrow.next { right: 14px; }
.lightbox-count { position: absolute; bottom: 18px; color: white; }

.related-article { position: relative; display: flex; flex-direction: column; gap: 4px; padding: 14px 42px 14px 0; border-top: 1px solid var(--border); }
.related-title { color: var(--text1) !important; font-weight: 650; line-height: 1.35; text-decoration: none; }
.related-title:hover { text-decoration: underline; }
.related-source, .muted { color: var(--text2); font-size: .9rem; }
.bookmark-button { position: absolute; top: 12px; right: 0; border: 0; background: transparent; color: var(--text1); font-size: 1.1rem; padding: 8px; cursor: pointer; }
@media (min-width: 1200px) {
	.game-detail-grid { grid-template-columns: minmax(0, 1fr) 360px; gap: 42px; }
	.preview-row { margin-inline: 0; padding: 2px 0 14px; }
}
</style>
